import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Trash2, Plus } from 'lucide-react';
import { WorkflowPlanConfig, PlanStep, AgentAction } from '@/types/workflow';

interface PlanConfigEditorProps {
  plan: WorkflowPlanConfig | undefined;
  onChange: (plan: WorkflowPlanConfig | undefined) => void;
  availableSteps: string[];
}

const ARTIFACT_TYPES = [
  'Document',
  'Cards',
  'Chart',
  'Table',
  'Timeline',
  'Board',
  'Mindmap',
  'Checklist',
  'Map'
];

export const PlanConfigEditor: React.FC<PlanConfigEditorProps> = ({
  plan,
  onChange,
  availableSteps
}) => {
  const handleTogglePlan = (enabled: boolean) => {
    if (enabled && !plan) {
      onChange({
        title: '',
        steps: [],
        agentGenerated: false
      });
    } else if (!enabled) {
      onChange(undefined);
    }
  };

  const handlePlanChange = (updates: Partial<WorkflowPlanConfig>) => {
    if (!plan) return;
    onChange({ ...plan, ...updates });
  };

  const addPlanStep = () => {
    if (!plan) return;
    const newStep: PlanStep = {
      id: `step${plan.steps.length + 1}`,
      title: '', // Display title for the step
      aim: '',
      complete: 'manual',
      // Default to agent task mode
      agentTask: {
        instruction: '',
        outputType: 'Document',
        context: []
      }
    };
    onChange({
      ...plan,
      steps: [...plan.steps, newStep]
    });
  };

  const updatePlanStep = (index: number, updates: Partial<PlanStep>) => {
    if (!plan) return;
    const steps = [...plan.steps];
    steps[index] = { ...steps[index], ...updates } as PlanStep;
    onChange({ ...plan, steps });
  };

  const removePlanStep = (index: number) => {
    if (!plan) return;
    onChange({
      ...plan,
      steps: plan.steps.filter((_, i) => i !== index)
    });
  };

  const toggleStepMode = (index: number, mode: 'from' | 'agent') => {
    if (!plan) return;

    if (mode === 'from') {
      updatePlanStep(index, {
        from: '',
        agentTask: undefined
      });
    } else {
      updatePlanStep(index, {
        from: undefined,
        agentTask: {
          instruction: '',
          outputType: 'Document',
          context: []
        }
      });
    }
  };

  const addContext = (stepIndex: number, contextValue: string) => {
    if (!plan) return;
    const step = plan.steps[stepIndex];
    if (!step?.agentTask) return;

    if (!step.agentTask.context.includes(contextValue)) {
      updatePlanStep(stepIndex, {
        agentTask: {
          ...step.agentTask,
          context: [...step.agentTask.context, contextValue]
        }
      });
    }
  };

  const removeContext = (stepIndex: number, contextIndex: number) => {
    if (!plan) return;
    const step = plan.steps[stepIndex];
    if (!step?.agentTask) return;

    updatePlanStep(stepIndex, {
      agentTask: {
        ...step.agentTask,
        context: step.agentTask.context.filter((_, i) => i !== contextIndex)
      }
    });
  };

  const addAgentAction = (stepIndex: number) => {
    if (!plan) return;
    const step = plan.steps[stepIndex];
    if (!step?.agentTask) return;

    const newAction: AgentAction = {
      type: 'agent',
      id: `action${(step.agentTask.agentActions?.length || 0) + 1}`,
      label: '',
      instruction: '',
      metadata: {},
      scope: 'item', // Default to 'item' (requires selection)
      source: 'workflow',
      order: 100 // Default to 100 (workflow actions typically appear first with higher numbers)
    };

    updatePlanStep(stepIndex, {
      agentTask: {
        ...step.agentTask,
        agentActions: [...(step.agentTask.agentActions || []), newAction]
      }
    });
  };

  const updateAgentAction = (stepIndex: number, actionIndex: number, updates: Partial<AgentAction>) => {
    if (!plan) return;
    const step = plan.steps[stepIndex];
    if (!step?.agentTask) return;

    const agentActions = [...(step.agentTask.agentActions || [])];
    agentActions[actionIndex] = { ...agentActions[actionIndex], ...updates } as AgentAction;

    updatePlanStep(stepIndex, {
      agentTask: {
        ...step.agentTask,
        agentActions
      }
    });
  };

  const removeAgentAction = (stepIndex: number, actionIndex: number) => {
    if (!plan) return;
    const step = plan.steps[stepIndex];
    if (!step?.agentTask) return;

    updatePlanStep(stepIndex, {
      agentTask: {
        ...step.agentTask,
        agentActions: step.agentTask.agentActions?.filter((_, i) => i !== actionIndex)
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <Checkbox
          id="enablePlan"
          checked={!!plan}
          onCheckedChange={handleTogglePlan}
        />
        <Label htmlFor="enablePlan" className="text-base font-semibold">
          Enable Plan Generation
        </Label>
      </div>

      {plan && (
        <Card>
          <CardHeader>
            <CardTitle>Plan Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Plan Title */}
            <div className="space-y-2">
              <Label htmlFor="planTitle">Plan Title</Label>
              <Input
                id="planTitle"
                value={plan.title}
                onChange={(e) => handlePlanChange({ title: e.target.value })}
                placeholder="e.g., My Action Plan for {{today}}"
              />
              <p className="text-xs text-muted-foreground">
                You can use template variables like {"{{today}}"}, {"{{workflow.stepId.value}}"}
              </p>
            </div>

            {/* Agent Generated Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="agentGenerated"
                checked={plan.agentGenerated || false}
                onCheckedChange={(checked) => handlePlanChange({ agentGenerated: !!checked })}
              />
              <Label htmlFor="agentGenerated">Agent Generated (use backend API)</Label>
            </div>

            {/* Plan Steps */}
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="text-base font-semibold">Plan Steps</Label>
                <Button type="button" variant="outline" size="sm" onClick={addPlanStep}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Step
                </Button>
              </div>

              {plan.steps.map((planStep, index) => (
                <Card key={index} className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4 space-y-4">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium">Step {index + 1}</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePlanStep(index)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label>Step ID</Label>
                      <Input
                        value={planStep.id}
                        onChange={(e) => updatePlanStep(index, { id: e.target.value })}
                        placeholder="e.g., createDraft"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Title (Display Name) *</Label>
                      <Input
                        value={planStep.title}
                        onChange={(e) => updatePlanStep(index, { title: e.target.value })}
                        placeholder="e.g., Choose your meal"
                      />
                      <p className="text-xs text-muted-foreground">
                        This will be shown as the step title in the plan
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Aim (Purpose)</Label>
                      <Input
                        value={planStep.aim}
                        onChange={(e) => updatePlanStep(index, { aim: e.target.value })}
                        placeholder="Purpose of this step"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Why (Optional)</Label>
                      <Input
                        value={planStep.why || ''}
                        onChange={(e) => updatePlanStep(index, { why: e.target.value })}
                        placeholder="Explanation for the user"
                      />
                    </div>

                    {/* Step Mode Selection */}
                    <div className="space-y-2">
                      <Label>Step Source</Label>
                      <Select
                        value={planStep.from !== undefined ? 'from' : 'agent'}
                        onValueChange={(value: 'from' | 'agent') => toggleStepMode(index, value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="from">Reference Workflow Step</SelectItem>
                          <SelectItem value="agent">Agent Generated Artifact</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* From Workflow Step */}
                    {planStep.from !== undefined && (
                      <div className="space-y-2 p-3 border rounded-lg">
                        <Label>Workflow Step Reference</Label>
                        <Select
                          value={planStep.from}
                          onValueChange={(value) => updatePlanStep(index, { from: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select workflow step" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableSteps.map(stepId => (
                              <SelectItem key={stepId} value={`steps.${stepId}`}>
                                steps.{stepId}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Agent Task */}
                    {planStep.agentTask && (
                      <div className="space-y-4 p-3 border rounded-lg bg-blue-50/50">
                        <div className="space-y-2">
                          <Label>Instruction Template</Label>
                          <Textarea
                            value={planStep.agentTask.instruction}
                            onChange={(e) => updatePlanStep(index, {
                              agentTask: {
                                ...planStep.agentTask!,
                                instruction: e.target.value
                              }
                            })}
                            placeholder="Use {{plan.stepId.value}} for template variables"
                            rows={3}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Output Artifact Type</Label>
                          <Select
                            value={planStep.agentTask.outputType}
                            onValueChange={(value) => updatePlanStep(index, {
                              agentTask: {
                                ...planStep.agentTask!,
                                outputType: value
                              }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ARTIFACT_TYPES.map(type => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Context (Plan References)</Label>
                          <Select
                            value=""
                            onValueChange={(value) => addContext(index, value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Add context" />
                            </SelectTrigger>
                            <SelectContent>
                              {plan.steps.slice(0, index).map((s, i) => (
                                <SelectItem key={i} value={`plan.${s.id}.selected`}>
                                  plan.{s.id}.selected
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {planStep.agentTask.context.map((ctx, ctxIdx) => (
                              <div key={ctxIdx} className="flex items-center gap-1 bg-white border rounded px-2 py-1">
                                <span className="text-sm">{ctx}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0"
                                  onClick={() => removeContext(index, ctxIdx)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Agent Actions */}
                        <div className="space-y-2 pt-4 border-t">
                          <div className="flex justify-between items-center">
                            <Label className="text-base font-medium">UI Actions</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => addAgentAction(index)}
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Action
                            </Button>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Add interactive actions (pills) to the created artifact
                          </p>

                          {planStep.agentTask.agentActions && planStep.agentTask.agentActions.length > 0 && (
                            <div className="space-y-3">
                              {planStep.agentTask.agentActions.map((action, actionIdx) => (
                                <Card key={actionIdx} className="p-3 bg-white/50">
                                  <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                      <h5 className="text-sm font-medium">Action {actionIdx + 1}</h5>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeAgentAction(index, actionIdx)}
                                        className="h-6 w-6 p-0 text-red-600"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="space-y-1">
                                        <Label className="text-xs">ID *</Label>
                                        <Input
                                          value={action.id}
                                          onChange={(e) => updateAgentAction(index, actionIdx, { id: e.target.value })}
                                          placeholder="e.g., select-meal"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Label *</Label>
                                        <Input
                                          value={action.label}
                                          onChange={(e) => updateAgentAction(index, actionIdx, { label: e.target.value })}
                                          placeholder="e.g., ➕ Add"
                                        />
                                      </div>
                                    </div>

                                    {/* Component Action Reference (offline toggle) */}
                                    <div className="space-y-1">
                                      <Label className="text-xs">Component Action (offline toggle)</Label>
                                      <Select
                                        value={action.componentActionRef || '_none'}
                                        onValueChange={(value) => updateAgentAction(index, actionIdx, {
                                          componentActionRef: value === '_none' ? undefined : value
                                        })}
                                      >
                                        <SelectTrigger className="h-8">
                                          <SelectValue placeholder="None (send to agent)" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="_none">None (send to agent)</SelectItem>
                                          <SelectItem value="toggle-favorite">toggle-favorite (Cards)</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <p className="text-[10px] text-muted-foreground">
                                        Use component action for offline toggles. Instruction is ignored when set.
                                      </p>
                                    </div>

                                    {/* Alternate Label/Icon (for toggle actions) */}
                                    {action.componentActionRef && (
                                      <div className="grid grid-cols-2 gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                                        <div className="space-y-1">
                                          <Label className="text-xs">Alt Label (when toggled)</Label>
                                          <Input
                                            value={action.labelAlt || ''}
                                            onChange={(e) => updateAgentAction(index, actionIdx, { labelAlt: e.target.value })}
                                            placeholder="e.g., ➖ Remove"
                                            className="h-8"
                                          />
                                        </div>
                                        <div className="space-y-1">
                                          <Label className="text-xs">Alt Icon (when toggled)</Label>
                                          <Input
                                            value={action.iconAlt || ''}
                                            onChange={(e) => updateAgentAction(index, actionIdx, { iconAlt: e.target.value })}
                                            placeholder="e.g., minus"
                                            className="h-8"
                                          />
                                        </div>
                                      </div>
                                    )}

                                    <div className="grid grid-cols-3 gap-2">
                                      <div className="space-y-1">
                                        <Label className="text-xs">Icon (optional)</Label>
                                        <Input
                                          value={action.icon || ''}
                                          onChange={(e) => updateAgentAction(index, actionIdx, { icon: e.target.value })}
                                          placeholder="e.g., plus"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Variant</Label>
                                        <Select
                                          value={action.variant || 'default'}
                                          onValueChange={(value: any) => updateAgentAction(index, actionIdx, { variant: value })}
                                        >
                                          <SelectTrigger className="h-8">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="default">Default</SelectItem>
                                            <SelectItem value="primary">Primary</SelectItem>
                                            <SelectItem value="destructive">Destructive</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Order</Label>
                                        <Input
                                          type="number"
                                          value={action.order ?? 100}
                                          onChange={(e) => updateAgentAction(index, actionIdx, { order: parseInt(e.target.value) || 100 })}
                                          placeholder="100"
                                          className="h-8"
                                        />
                                        <p className="text-[10px] text-muted-foreground">Higher = first</p>
                                      </div>
                                    </div>

                                    {/* Instruction - only required when no componentActionRef */}
                                    <div className="space-y-1">
                                      <Label className="text-xs">
                                        Instruction Template {action.componentActionRef ? '(ignored)' : '*'}
                                      </Label>
                                      <Textarea
                                        value={action.instruction}
                                        onChange={(e) => updateAgentAction(index, actionIdx, { instruction: e.target.value })}
                                        placeholder="User selected {{selectedItem.title}}. Complete step {{stepId}} and continue."
                                        rows={2}
                                        className={`text-xs ${action.componentActionRef ? 'opacity-50' : ''}`}
                                        disabled={!!action.componentActionRef}
                                      />
                                      <p className="text-xs text-muted-foreground">
                                        {action.componentActionRef
                                          ? 'Instruction is ignored when using a component action'
                                          : <>Use {"{{selectedItem.*}}"}, {"{{planId}}"}, {"{{stepId}}"}, {"{{artifactId}}"}</>
                                        }
                                      </p>
                                    </div>

                                    <div className="space-y-2">
                                      <Label htmlFor={`action-${actionIdx}-scope`} className="text-xs">
                                        Action Scope
                                      </Label>
                                      <Select
                                        value={action.scope || 'item'}
                                        onValueChange={(value: 'item' | 'artifact' | 'both') =>
                                          updateAgentAction(index, actionIdx, { scope: value })
                                        }
                                      >
                                        <SelectTrigger id={`action-${actionIdx}-scope`} className="text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="item">Item (requires selection)</SelectItem>
                                          <SelectItem value="artifact">Artifact (always visible)</SelectItem>
                                          <SelectItem value="both">Both (context varies)</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <p className="text-xs text-muted-foreground">
                                        Item: Show only when item selected | Artifact: Always visible | Both: Always visible with dynamic context
                                      </p>
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Condition and Complete */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Condition (Optional)</Label>
                        <Input
                          value={planStep.condition || ''}
                          onChange={(e) => updatePlanStep(index, { condition: e.target.value })}
                          placeholder="e.g., workflow.stepId === 'value'"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Complete Trigger</Label>
                        <Select
                          value={planStep.complete || 'manual'}
                          onValueChange={(value) => updatePlanStep(index, { complete: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual">Manual</SelectItem>
                            <SelectItem value="onSelect">On Select</SelectItem>
                            <SelectItem value="onEdit">On Edit</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {plan.steps.length === 0 && (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <p className="text-muted-foreground">No plan steps yet</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={addPlanStep}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add First Step
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
